import { useState } from "react";
import { Download, PlayCircle, Save, ShieldAlert, ShieldBan } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { MdListUpload } from "./MdListUpload";
import { ScreeningStatusBoard } from "./ScreeningStatusBoard";

const resultClass = (result) => `compliance-result compliance-result-${(result || "none").toLowerCase().replace(/ /g, "-")}`;

// MD Medicaid column: Match / Verify license (LB) / Clear; DC column: this month's checklist result.
const StateBadge = ({ value, testId }) => <em className={resultClass(value || "Not checked")} data-testid={testId}>{value || "Not checked"}</em>;

const HoldBadge = ({ provider }) => {
  const hold = provider.hold;
  if (!hold?.active) return null;
  return (
    <em className="compliance-result compliance-result-match compliance-hold" data-testid={`hold-badge-${provider.id}`}>
      <ShieldBan size={11} /> {hold.level === "confirmed" ? "Removed from active assignments" : "New assignments blocked"} · {hold.sources.join(", ")}
    </em>
  );
};

async function downloadHistory() {
  const response = await api.get("/compliance/exclusions/export.csv", { responseType: "blob" });
  const url = URL.createObjectURL(response.data);
  const link = Object.assign(document.createElement("a"), { href: url, download: "tagforce-exclusion-screening-history.csv" });
  link.click();
  URL.revokeObjectURL(url);
}

// Badge for the OIG / SAM columns: amber "Possible match" while unreviewed, red "Match" only after HR confirms.
const SourceBadge = ({ provider, source, testId }) => {
  const result = provider.screening?.[source];
  const confirmed = provider.review?.disposition === "confirmed";
  if (result === "Match") {
    return <em className={confirmed ? "compliance-result compliance-result-match" : "compliance-result compliance-result-pending"} data-testid={testId}>{confirmed ? "Match" : "Possible match"}</em>;
  }
  return <em className={resultClass(result)} data-testid={testId}>{result || "Not checked"}</em>;
};

const ManualCheckForm = ({ provider, onSaved }) => {
  const existing = provider.state_check || {};
  const [form, setForm] = useState({ state: existing.state || "DC", list_name: existing.list_name || "", result: existing.result || "Clear", checked_on: existing.checked_on || "", note: existing.note || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await api.put(`/compliance/exclusions/${provider.id}/manual`, form);
      onSaved(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="compliance-manual-form" data-testid={`manual-check-form-${provider.id}`} onSubmit={save}>
      <select value={form.state} data-testid={`manual-state-${provider.id}`} onChange={(e) => setForm({ ...form, state: e.target.value })}>
        <option>DC</option><option>Maryland</option><option>Virginia</option><option>Other</option>
      </select>
      <input placeholder="List checked (e.g. DC Medicaid exclusions)" value={form.list_name} data-testid={`manual-list-${provider.id}`} onChange={(e) => setForm({ ...form, list_name: e.target.value })} />
      <select value={form.result} data-testid={`manual-result-${provider.id}`} onChange={(e) => setForm({ ...form, result: e.target.value })}>
        <option>Clear</option><option>Match</option><option>Not checked</option>
      </select>
      <input type="date" value={form.checked_on} data-testid={`manual-date-${provider.id}`} onChange={(e) => setForm({ ...form, checked_on: e.target.value })} />
      <input placeholder="Note" value={form.note} data-testid={`manual-note-${provider.id}`} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      <button className="button button-ghost" data-testid={`manual-save-${provider.id}`} disabled={busy}><Save size={13} /> Record</button>
      {error && <small className="form-message error-message">{error}</small>}
    </form>
  );
};

const matchSummary = (match) => match.matched_on === "NPI" ? `NPI ${match.npi}` : match.matched_on === "license number" ? `License ${match.license}` : [match.state, match.specialty || match.general || match.exclusion_type || match.sanction_type, match.profession, match.agency, match.excldate || match.activation_date || match.date, match.location].filter(Boolean).join(" · ");

const MatchReview = ({ provider, methods, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("");
  const [confirming, setConfirming] = useState(false);
  const screening = provider.screening || {};
  const matches = [...(screening.matches || []), ...(provider.md?.matches || []).filter((m) => !m.license_based)];
  const noteReady = note.trim().length >= 5;
  async function review(disposition) {
    setBusy(true);
    try {
      await api.put(`/compliance/exclusions/${provider.id}/review`, { disposition, note: note.trim(), method });
      setConfirming(false);
      await onSaved();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="compliance-match-review" data-testid={`match-review-${provider.id}`}>
      <p className="compliance-match-review-title">Possible match — new assignments are blocked until HR clears it. Verify against DOB, SSN last four, NPI or license number, then record how it was cleared.</p>
      <ul>
        {matches.map((match) => <li key={match.record_key}>{match.business || match.name || `${match.first} ${match.last}`} — {matchSummary(match)}</li>)}
        {provider.dc?.result === "Possible match" && <li>DC Excluded Parties List — {provider.dc.note || "possible match recorded by HR"}</li>}
      </ul>
      <div className="compliance-clear-form">
        <label className="compliance-review-note">
          Verified with
          <select value={method} data-testid={`review-method-${provider.id}`} onChange={(e) => setMethod(e.target.value)}>
            <option value="">Choose…</option>
            {(methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="compliance-review-note">
          Review note (required to clear)
          <input value={note} placeholder="e.g. DOB and address do not match" maxLength={600} data-testid={`review-note-${provider.id}`} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
      {!confirming ? (
        <div className="button-row">
          <button type="button" className="button button-secondary" data-testid={`dismiss-match-${provider.id}`} disabled={busy || !noteReady || !method} title={noteReady && method ? "" : "Choose what you verified with and add a short note"} onClick={() => review("not_same_person")}>Not the same person — clear</button>
          <button type="button" className="button button-secondary" data-testid={`confirm-match-${provider.id}`} disabled={busy} onClick={() => setConfirming(true)}>Confirm match</button>
        </div>
      ) : (
        <div className="compliance-confirm-step" data-testid={`confirm-step-${provider.id}`}>
          <p><b>Mark {provider.name} as excluded?</b> This ends every active assignment immediately, blocks new ones and alerts the CEO and HR right away.</p>
          <div className="button-row">
            <button type="button" className="button button-primary compliance-danger-button" data-testid={`confirm-match-yes-${provider.id}`} disabled={busy} onClick={() => review("confirmed")}>Yes, confirm exclusion</button>
            <button type="button" className="button button-secondary" data-testid={`confirm-match-cancel-${provider.id}`} disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        </div>
      )}
      {error && <small className="form-message error-message" data-testid={`review-error-${provider.id}`}>{error}</small>}
    </div>
  );
};

const ReviewHistory = ({ provider }) => {
  const [open, setOpen] = useState(false);
  const history = provider.review_history || [];
  if (!history.length) return null;
  return (
    <div className="compliance-review-history" data-testid={`review-history-${provider.id}`}>
      <button type="button" className="table-edit-button compliance-text-button" data-testid={`toggle-review-history-${provider.id}`} onClick={() => setOpen(!open)}>{open ? "Hide" : "Show"} review history ({history.length})</button>
      {open && (
        <ol>
          {history.map((entry) => (
            <li key={entry.id} data-testid={`review-entry-${entry.id}`}>
              <b>{entry.disposition === "not_same_person" ? `Cleared — not the same person${entry.method ? ` (${entry.method})` : ""}` : "Match confirmed"}</b> by {entry.reviewed_by_name} on {new Date(entry.reviewed_at).toLocaleString()}
              {entry.note && <> — “{entry.note}”</>}
              {entry.matches?.length ? <small>{entry.matches.map((m) => m.business || m.name || `${m.first} ${m.last}`).join("; ")}</small> : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export const ExclusionScreeningPanel = ({ data, canRun, onRefresh }) => {
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);

  async function exportHistory() {
    setExporting(true);
    try {
      await downloadHistory();
    } catch (requestError) {
      setNotice(formatApiError(requestError));
    } finally {
      setExporting(false);
    }
  }
  const last = data.last_run;
  const columns = canRun ? 8 : 7;

  async function runNow() {
    setRunning(true);
    try {
      const response = await api.post("/compliance/exclusions/run");
      setNotice(`Screened ${response.data.providers_checked} people: ${response.data.leie_matches} LEIE match(es), ${response.data.sam_matches} SAM.gov match(es), ${response.data.md_matches ?? 0} MD Medicaid match(es), ${response.data.md_verify_license ?? 0} MD license check(s)${response.data.sam_skipped ? ` · SAM.gov skipped for ${response.data.sam_skipped} (API key not configured)` : ""}.`);
      await onRefresh();
    } catch (requestError) {
      setNotice(formatApiError(requestError));
    } finally {
      setRunning(false);
    }
  }

  return (
    <section className="compliance-card" data-testid="exclusion-screening-panel">
      <div className="section-heading">
        <div>
          <h2><ShieldAlert size={17} /> Exclusion screening</h2>
          <p>
            Every active person — providers, assistants, aides and staff — is checked monthly against the federal OIG LEIE, SAM.gov exclusions, the Maryland Medicaid Sanctioned Provider List and the DC Excluded Parties List. Any possible match blocks new assignments until HR clears it; a confirmed match removes the person from active assignments and alerts the CEO and HR immediately.
            {!data.sam_configured && " SAM.gov checks are skipped until a SAM_GOV_API_KEY is configured."}
            {data.sam_configured && data.sam_key_expires && ` SAM.gov API key expires ${data.sam_key_expires}${data.sam_key_days_left !== null && data.sam_key_days_left <= 14 ? " — renew soon" : ""}.`}
          </p>
          <p className="pairing-meta" data-testid="exclusion-last-run">
            {last ? `Last run ${new Date(last.started_at).toLocaleString()} (${last.trigger}) · ${last.providers_checked} checked · ${last.leie_matches + last.sam_matches + (last.md_matches || 0)} match(es)` : "No screening run recorded yet."}
          </p>
        </div>
        <div className="button-row">
          <button type="button" className="button button-secondary" data-testid="export-exclusion-history" disabled={exporting} onClick={exportHistory}><Download size={14} /> {exporting ? "Exporting…" : "Export history (CSV)"}</button>
          {canRun && <button type="button" className="button button-primary" data-testid="run-exclusion-screening" disabled={running} onClick={runNow}><PlayCircle size={15} /> {running ? "Screening…" : "Run screening now"}</button>}
        </div>
      </div>
      <ScreeningStatusBoard status={data.month_status} />
      <MdListUpload current={data.md_list} canRun={canRun} onUploaded={onRefresh} />
      {notice && <p className="form-message success-message" data-testid="exclusion-notice">{notice}</p>}
      <div className="credential-table-wrap exclusion-table-wrap">
        <table className="exclusion-table exclusion-table-wide">
          <colgroup>
            <col className="exclusion-col-provider" />
            <col className="exclusion-col-result" />
            <col className="exclusion-col-result" />
            <col className="exclusion-col-result" />
            <col className="exclusion-col-result" />
            <col className="exclusion-col-date" />
            <col className="exclusion-col-manual" />
            {canRun && <col className="exclusion-col-actions" />}
          </colgroup>
          <thead><tr><th>Person</th><th>OIG LEIE</th><th>SAM.gov</th><th>MD Medicaid</th><th>DC EPL</th><th>Last checked</th><th>Other state list (manual)</th>{canRun && <th aria-label="Actions" />}</tr></thead>
          <tbody>
            {data.providers.map((provider) => {
              const unreviewed = provider.screening?.result === "Match" || provider.md?.result === "Match" || (provider.hold?.active && provider.hold.level !== "confirmed");
              const confirmed = provider.review?.disposition === "confirmed" || provider.hold?.level === "confirmed";
              return (
                <ExclusionRows key={provider.id} canRun={canRun} columns={columns} confirmed={confirmed} editing={editing} methods={data.clearance_methods} provider={provider} unreviewed={unreviewed} onEdit={setEditing} onRefresh={onRefresh} />
              );
            })}
          </tbody>
        </table>
        {!data.providers.length && <p className="operations-empty-state">No active people to screen.</p>}
      </div>
    </section>
  );
};

const ExclusionRows = ({ provider, canRun, columns, confirmed, unreviewed, editing, methods, onEdit, onRefresh }) => (
  <>
    <tr className={unreviewed ? `is-match ${confirmed ? "is-confirmed" : "is-possible"}` : ""} data-testid={`exclusion-row-${provider.id}`}>
      <td>
        <b>{provider.name}</b><span>{provider.discipline}</span>
        <HoldBadge provider={provider} />
        {unreviewed && !provider.hold?.active && (
          <em className={`compliance-result ${confirmed ? "compliance-result-match" : "compliance-result-pending"}`} data-testid={`match-confidence-${provider.id}`}>
            {confirmed ? "Match confirmed" : "Possible match — HR review"}
          </em>
        )}
        {provider.md?.result === "Verify license" && <em className="compliance-result compliance-result-pending" data-testid={`md-verify-${provider.id}`}>Verify license with MD board</em>}
        {provider.review?.disposition === "not_same_person" && !provider.hold?.active && <small className="pairing-meta" data-testid={`match-dismissed-${provider.id}`}>Cleared as not the same person ({provider.review.method || "reviewed"}) · {provider.review.reviewed_by_name}</small>}
        {confirmed && <small className="pairing-meta" data-testid={`match-confirmed-${provider.id}`}>Match confirmed · {provider.hold?.confirmed_by_name || provider.review?.reviewed_by_name}</small>}
        <ReviewHistory provider={provider} />
      </td>
      <td><SourceBadge provider={provider} source="leie_result" testId={`leie-badge-${provider.id}`} /></td>
      <td><SourceBadge provider={provider} source="sam_result" testId={`sam-badge-${provider.id}`} /></td>
      <td><StateBadge value={provider.md?.result} testId={`md-badge-${provider.id}`} />{provider.md?.as_of && <small className="pairing-meta">as of {provider.md.as_of}</small>}</td>
      <td><StateBadge value={provider.dc?.result === "Pending" ? "Not checked" : provider.dc?.result} testId={`dc-badge-${provider.id}`} /></td>
      <td>{provider.screening ? new Date(provider.screening.checked_at).toLocaleDateString() : "—"}</td>
      <td data-testid={`manual-check-${provider.id}`}>
        {provider.state_check ? <><em className={resultClass(provider.state_check.result)}>{provider.state_check.result}</em> <span>{provider.state_check.state} · {provider.state_check.checked_on} · {provider.state_check.checked_by_name}</span></> : <span className="pairing-meta">Not recorded</span>}
      </td>
      {canRun && <td className="credential-actions exclusion-actions"><button type="button" className="table-edit-button compliance-text-button" data-testid={`record-manual-${provider.id}`} onClick={() => onEdit(editing === provider.id ? null : provider.id)}>{editing === provider.id ? "Close" : "Record manual check"}</button></td>}
    </tr>
    {canRun && (editing === provider.id || (unreviewed && !confirmed)) && (
      <tr className="exclusion-detail-row" data-testid={`exclusion-detail-${provider.id}`}>
        <td colSpan={columns}>
          {editing === provider.id && <ManualCheckForm provider={provider} onSaved={async () => { onEdit(null); await onRefresh(); }} />}
          {unreviewed && !confirmed && <MatchReview provider={provider} methods={methods} onSaved={onRefresh} />}
        </td>
      </tr>
    )}
  </>
);
